/**
 * 2D Vector class for game math operations
 * Immutable design - all operations return new Vec2 instances
 */
export class Vec2 {
    x;
    y;
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    /** Create a zero vector */
    static zero() {
        return new Vec2(0, 0);
    }
    /** Create a vector from an angle (in radians) */
    static fromAngle(angle) {
        return new Vec2(Math.cos(angle), Math.sin(angle));
    }
    /** Create a random unit vector */
    static random() {
        const angle = Math.random() * Math.PI * 2;
        return Vec2.fromAngle(angle);
    }
    /** Create a vector from polar coordinates */
    static fromPolar(angle, length) {
        return new Vec2(Math.cos(angle) * length, Math.sin(angle) * length);
    }
    /** Add another vector */
    add(other) {
        return new Vec2(this.x + other.x, this.y + other.y);
    }
    /** Subtract another vector */
    sub(other) {
        return new Vec2(this.x - other.x, this.y - other.y);
    }
    /** Multiply by a scalar */
    scale(scalar) {
        return new Vec2(this.x * scalar, this.y * scalar);
    }
    /** Component-wise multiplication */
    mul(other) {
        return new Vec2(this.x * other.x, this.y * other.y);
    }
    /** Get the length (magnitude) of the vector */
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    /** Get the squared length (avoids sqrt for comparisons) */
    lengthSquared() {
        return this.x * this.x + this.y * this.y;
    }
    /** Get a normalized (unit length) version of this vector */
    normalize() {
        const len = this.length();
        if (len === 0)
            return Vec2.zero();
        return new Vec2(this.x / len, this.y / len);
    }
    /** Get the distance to another vector */
    distanceTo(other) {
        return this.sub(other).length();
    }
    /** Get the squared distance to another vector */
    distanceToSquared(other) {
        return this.sub(other).lengthSquared();
    }
    /** Get the angle of this vector (in radians) */
    angle() {
        return Math.atan2(this.y, this.x);
    }
    /** Get the angle to another vector (in radians) */
    angleTo(other) {
        return other.sub(this).angle();
    }
    /** Dot product with another vector */
    dot(other) {
        return this.x * other.x + this.y * other.y;
    }
    /** Cross product (returns scalar z-component in 2D) */
    cross(other) {
        return this.x * other.y - this.y * other.x;
    }
    /** Rotate the vector by an angle (in radians) */
    rotate(angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return new Vec2(this.x * cos - this.y * sin, this.x * sin + this.y * cos);
    }
    /** Get a perpendicular vector (rotated 90 degrees counter-clockwise) */
    perpendicular() {
        return new Vec2(-this.y, this.x);
    }
    /** Reflect this vector off a surface with the given normal */
    reflect(normal) {
        const d = this.dot(normal) * 2;
        return this.sub(normal.scale(d));
    }
    /** Linear interpolation to another vector */
    lerp(other, t) {
        return new Vec2(this.x + (other.x - this.x) * t, this.y + (other.y - this.y) * t);
    }
    /** Clamp the length of this vector */
    clampLength(maxLength) {
        const len = this.length();
        if (len <= maxLength)
            return this;
        return this.normalize().scale(maxLength);
    }
    /** Set the length of this vector */
    setLength(length) {
        return this.normalize().scale(length);
    }
    /** Check if this vector equals another (with optional epsilon) */
    equals(other, epsilon = 0.0001) {
        return (Math.abs(this.x - other.x) < epsilon &&
            Math.abs(this.y - other.y) < epsilon);
    }
    /** Clone this vector */
    clone() {
        return new Vec2(this.x, this.y);
    }
    /** Convert to array [x, y] */
    toArray() {
        return [this.x, this.y];
    }
    /** Create from array */
    static fromArray(arr) {
        return new Vec2(arr[0], arr[1]);
    }
    /** String representation */
    toString() {
        return `Vec2(${this.x.toFixed(2)}, ${this.y.toFixed(2)})`;
    }
}
//# sourceMappingURL=vec2.js.map